// @ts-nocheck
// Agent Portal (/cti2) — full "My Campaigns" dashboard section, rendered
// inline in AgentDashboardView, mirroring AgentGroupsSection.tsx — the
// Topbar's "Campaigns" chip scrolls down to this section instead of
// opening a popover (see Topbar.tsx's handleCampaignsSectionClick).
// Toggle logic duplicated from AgentContextStrip.tsx rather than shared,
// matching this codebase's convention for small self-contained logic.
import { useMemo, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { postApiCall } from '@/service/httpCall';
import { API_AGENT_DASHBOARD_CAMPAIGN_TOGGLE } from '@/service/url';
import { getUser } from '@/utilities/setting';
import { MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { hasPermission } from '@/utilities/helpers';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { useModuleOps } from '../hooks/useModuleOps';
import { HAIRLINE } from '../team/teamUi';
import { Switch } from '@/components/ui/switch';

function StatusDot({ active }) {
  return (
    <span className={`inline-block h-1.5 w-1.5 rounded-full ${active ? 'bg-success' : 'bg-muted-foreground/40'}`} />
  );
}

export default function AgentCampaignsSection({ campaigns = [], userData }) {
  const [search, setSearch] = usePersistedState('cti2/AgentCampaignsSection:search', '');
  const [togglingCampaignId, setTogglingCampaignId] = useState(null);
  const [campaignOverrides, setCampaignOverrides] = useState<Record<string, boolean>>({});

  const moduleOps = useModuleOps();
  const isSuperAdmin = getUser()?.role === USER_ROLES.SUPERADMIN;
  const canReadCampaigns = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.CAMPAIGNS, 'read');
  const canEditCampaigns = isSuperAdmin || hasPermission(moduleOps, MODULE_ACL.CAMPAIGNS, 'edit');

  const campaignsResolved = useMemo(
    () =>
      campaigns.map((c) => ({
        ...c,
        agent_campaign_active: campaignOverrides[c.campaign_id ?? c.id] ?? c.agent_campaign_active !== false,
      })),
    [campaigns, campaignOverrides],
  );

  const filteredCampaigns = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return campaignsResolved;
    return campaignsResolved.filter((c) => String(c.campaign_name || '').toLowerCase().includes(q));
  }, [campaignsResolved, search]);

  const agentRecordId = userData?.agentData?.id;

  const handleToggleCampaign = async (campaign, nextActive) => {
    const campaignId = campaign?.campaign_id ?? campaign?.id;
    if (!campaignId || !agentRecordId || togglingCampaignId) return;
    setTogglingCampaignId(campaignId);
    try {
      await postApiCall(API_AGENT_DASHBOARD_CAMPAIGN_TOGGLE, {
        agent_id: agentRecordId,
        campaign_id: campaignId,
        is_active: nextActive,
      });
      setCampaignOverrides((prev) => ({ ...prev, [campaignId]: nextActive }));
      toast.success(`Campaign turned ${nextActive ? 'on' : 'off'}`);
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Failed to toggle campaign.');
    } finally {
      setTogglingCampaignId(null);
    }
  };

  if (!canReadCampaigns) {
    return <div className="px-2 py-3 text-center text-[11px] text-muted-foreground">ACL permission missing for Campaigns.</div>;
  }

  return (
    <div className="space-y-2.5">
      {campaigns.length > 4 && (
        <div className={`flex items-center gap-1.5 rounded-lg border ${HAIRLINE} bg-muted/20 px-2.5 py-1.5`}>
          <Search className="h-3 w-3 shrink-0 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search campaigns…"
            className="w-full bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
      )}
      {filteredCampaigns.length === 0 ? (
        <div className="px-2 py-6 text-center text-xs text-muted-foreground">
          {search ? 'No campaigns match' : 'No active campaigns'}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3">
          {filteredCampaigns.map((c) => (
            <div
              key={c.campaign_id ?? c.id}
              className={`flex items-center gap-2 rounded-xl border ${HAIRLINE} bg-card p-3 shadow-sm`}
            >
              <StatusDot active={c.agent_campaign_active !== false} />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                {c.campaign_name || `Campaign #${c.campaign_id ?? c.id}`}
              </span>
              {canEditCampaigns ? (
                togglingCampaignId === (c.campaign_id ?? c.id) ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                ) : (
                  <Switch
                    checked={c.agent_campaign_active !== false}
                    onCheckedChange={(checked) => handleToggleCampaign(c, checked)}
                    className="scale-75"
                  />
                )
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
